import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Loader2, Plus, Trash2, Moon } from 'lucide-react';
import { WEEKDAY_LABELS, fmtWindow } from '../../../lib/scheduleFormat';

const emptyWindow = { weekday: 0, start_time: '05:00', end_time: '13:00' };

// A new agreement never rewrites the old one — the previous version is closed
// the day before this one starts, so history stays intact.
const AvailabilityDialog = ({ open, kitchen, version, onClose, onSubmit, busy }) => {
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [note, setNote] = useState('');
  const [windows, setWindows] = useState([emptyWindow]);

  useEffect(() => {
    if (!open) return;
    setEffectiveFrom(version?.effective_from || kitchen?.active_from || '');
    setNote(version?.note || '');
    setWindows(version?.windows?.length ? version.windows.map((w) => ({ ...w })) : [{ ...emptyWindow }]);
  }, [open, version, kitchen]);

  const setWindow = (index, patch) => {
    setWindows((prev) => prev.map((w, i) => (i === index ? { ...w, ...patch } : w)));
  };

  const crosses = (w) => w.end_time <= w.start_time;

  const submit = () => {
    onSubmit({
      effective_from: effectiveFrom,
      note,
      windows: windows.map((w) => ({
        weekday: Number(w.weekday),
        start_time: w.start_time,
        end_time: w.end_time,
      })),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="availability-dialog">
        <DialogHeader>
          <DialogTitle>{version ? 'Edit access agreement' : 'New access agreement'}</DialogTitle>
          <DialogDescription>
            The weekly hours the landlord lets you into {kitchen?.name}. Bookings can only ever sit
            inside these windows.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="availability-effective-from">Effective from</Label>
              <Input
                id="availability-effective-from"
                type="date"
                value={effectiveFrom}
                disabled={!!version}
                onChange={(e) => setEffectiveFrom(e.target.value)}
                data-testid="availability-effective-from"
              />
              {version && (
                <p className="mt-1 text-xs text-gray-500">
                  Start dates cannot move — add a new agreement instead.
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="availability-note">Note (optional)</Label>
              <Input
                id="availability-note"
                value={note}
                placeholder="e.g. renewed lease, 2026 terms"
                onChange={(e) => setNote(e.target.value)}
                data-testid="availability-note"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label>Weekly access windows</Label>
              <Button
                type="button" variant="outline" size="sm"
                onClick={() => setWindows((prev) => [...prev, { ...emptyWindow }])}
                data-testid="availability-add-window"
              >
                <Plus className="h-4 w-4 mr-1" /> Add window
              </Button>
            </div>
            <p className="mt-1 mb-3 text-xs text-gray-500">
              An end time earlier than the start means the window runs past midnight into the next day.
            </p>

            <div className="space-y-2">
              {windows.map((w, index) => (
                <div key={index} className="flex flex-wrap items-end gap-2 rounded-lg border border-gray-200 p-2"
                  data-testid={`availability-window-${index}`}>
                  <div className="min-w-[7rem]">
                    <Label className="text-xs text-gray-500">Day</Label>
                    <select
                      className="mt-1 h-10 w-full rounded-md border border-gray-300 bg-white px-2 text-sm"
                      value={w.weekday}
                      onChange={(e) => setWindow(index, { weekday: Number(e.target.value) })}
                      data-testid={`availability-window-weekday-${index}`}
                    >
                      {WEEKDAY_LABELS.map((label, wd) => (
                        <option key={wd} value={wd}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Opens</Label>
                    <Input
                      type="time" className="mt-1 w-[7.5rem]" value={w.start_time}
                      onChange={(e) => setWindow(index, { start_time: e.target.value })}
                      data-testid={`availability-window-start-${index}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Closes</Label>
                    <Input
                      type="time" className="mt-1 w-[7.5rem]" value={w.end_time}
                      onChange={(e) => setWindow(index, { end_time: e.target.value })}
                      data-testid={`availability-window-end-${index}`}
                    />
                  </div>
                  <div className="flex-1 min-w-[8rem] pb-2 text-xs text-gray-600">
                    {fmtWindow(w.start_time, w.end_time)}
                    {crosses(w) && (
                      <Badge variant="outline" className="ml-2 bg-slate-50 text-slate-700">
                        <Moon className="h-3 w-3 mr-1" /> past midnight
                      </Badge>
                    )}
                  </div>
                  {windows.length > 1 && (
                    <Button
                      type="button" variant="ghost" size="icon" className="mb-1 text-red-600"
                      onClick={() => setWindows((prev) => prev.filter((_, i) => i !== index))}
                      data-testid={`availability-remove-window-${index}`}
                      aria-label="Remove window"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="availability-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || !effectiveFrom}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="availability-save"
          >
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {version ? 'Save agreement' : 'Add agreement'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AvailabilityDialog;
